import React, { useEffect, useState } from 'react';
import { Sun, Heart, Coins, PersonStanding, MinusCircle, CalendarDays, ChevronRight, Ban } from 'lucide-react';
import { useCountUp, reducedMotion } from '../../shared/Motion';

const iconFor = (b) => {
  const k = `${b.leave_type_id} ${b.leave_type_name}`.toLowerCase();
  if (k.includes('earned') || k.includes('casual') || k.includes('annual')) return Sun;
  if (k.includes('sick')) return Heart;
  if (k.includes('comp')) return Coins;
  if (k.includes('matern') || k.includes('patern')) return PersonStanding;
  if (k.includes('unpaid') || k.includes('loss')) return MinusCircle;
  return CalendarDays;
};

const fmt = (n) => (Number.isInteger(Number(n)) ? Number(n) : Number(n).toFixed(1));

// LEAVE-UI-001: premium balance card. Selection/disabled semantics are unchanged (button; disabled when available === 0).
const BalanceCard = ({ balance: b, selected, onSelect, delay }) => {
  const Icon = iconFor(b);
  const exhausted = b.available === 0;
  const pct = b.total_quota > 0 ? Math.min(100, Math.round((b.used / b.total_quota) * 100)) : 0;
  const [w, setW] = useState(reducedMotion() ? pct : 0);
  useEffect(() => { const id = requestAnimationFrame(() => setW(pct)); return () => cancelAnimationFrame(id); }, [pct]);
  const left = useCountUp(Number(b.available) || 0, 700);
  const color = b.color || '#6d4ee0';
  return (
    <button type="button" disabled={exhausted} onClick={onSelect} data-testid={`leave-type-card-${b.leave_type_id}`} aria-pressed={selected}
      className={`lv-card lv-lift an-rise group relative w-full text-left px-4 pt-3 pb-2.5 h-full disabled:cursor-not-allowed ${selected ? 'ring-2 ring-violet-400/70 border-violet-300' : ''} ${exhausted ? 'lv-exhausted' : ''}`}
      style={{ animationDelay: `${delay}ms`, background: exhausted ? 'linear-gradient(180deg,#fff, #f8fafc)' : `linear-gradient(180deg, #fff 0%, ${color}12 100%)`, borderColor: selected ? undefined : `${color}33` }}>
      <div className="flex items-center gap-3">
        <span className="h-11 w-11 shrink-0 rounded-full flex items-center justify-center" style={{ background: exhausted ? '#e2e8f0' : `${color}24`, color: exhausted ? '#64748b' : color }}>
          <Icon className="h-5 w-5" strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-slate-800 leading-tight break-words">{b.leave_type_name}</p>
          <p className="text-[22px] font-extrabold tracking-tight leading-none mt-0.5 whitespace-nowrap" style={{ color: exhausted ? '#475569' : color }} data-testid={`leave-type-left-${b.leave_type_id}`}>
            {fmt(left)} <span className="text-[15px] font-bold">left</span>
          </p>
        </div>
        <span className="text-slate-400 group-hover:text-slate-600 transition-colors" aria-hidden="true">
          {exhausted ? <Ban className="h-4 w-4 text-slate-300" /> : <ChevronRight className="h-4.5 w-4.5" />}
        </span>
      </div>
      <div className="mt-2.5 h-[5px] w-full rounded-full bg-slate-200/70 overflow-hidden">
        <div className="lv-bar h-full rounded-full" style={{ width: `${w}%`, background: exhausted ? '#94a3b8' : color }} />
      </div>
      <div className="mt-1 flex items-center justify-between text-[11.5px] text-slate-500">
        <span data-testid={`leave-type-used-${b.leave_type_id}`}>{fmt(b.used)} / {fmt(b.total_quota)} used</span>
        {b.pending > 0 && <span className="text-amber-600 font-medium">{fmt(b.pending)} pending</span>}
        {b.paid === false && <span className="text-slate-400">Unpaid</span>}
      </div>
    </button>
  );
};

const spanFor = (n, i) => {
  const lastRowStart = n - (n % 3 || 3);
  if (i < lastRowStart) return 'lg:col-span-2';
  const r = n - lastRowStart;
  return r === 1 ? 'lg:col-span-6' : r === 2 ? 'lg:col-span-3' : 'lg:col-span-2';
};

export const LeaveBalanceCards = ({ balances = [], selectedId, onSelect }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3" data-testid="leave-balance-cards">
    {balances.map((b, i) => (
      <div key={b.leave_type_id} className={spanFor(balances.length, i)}>
        <BalanceCard balance={b} selected={selectedId === b.leave_type_id} onSelect={() => onSelect(b.leave_type_id)} delay={80 + i * 60} />
      </div>
    ))}
  </div>
);

export default LeaveBalanceCards;
